import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

/**
 * CockpitEmptyState — Hero-State für User ohne Fokustyp-Test.
 *
 * Bug-Fix Mini-Hotfix 29.06.2026:
 * Statt 4 leerer Kacheln (Activity/Heatmap/Timeline/HotTips) zeigen wir
 * für frische User OHNE `primary_fokustyp` einen einzigen prominenten
 * CTA-Block. Das ist die wichtigste Aktion zu diesem Zeitpunkt.
 *
 * Wird gerendert anstelle des Quad-Grids (nicht zusätzlich) — die User
 * sehen also bewusst NICHT die leeren Tiles.
 *
 * Dismiss („Oder später") ist Session-only (sessionStorage). Nach Reload
 * kommt der Empty-State wieder, bis der User den Test gemacht hat.
 */
const DISMISS_KEY = "f4s_cockpit_empty_state_dismissed";

export default function CockpitEmptyState() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = React.useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch (_) {
      return false;
    }
  });

  const onStart = () => navigate("/dashboard/test");
  const onLater = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {
      /* noop */
    }
    setDismissed(true);
  };

  if (dismissed) {
    return (
      <div
        data-testid="cockpit-empty-state-dismissed-hint"
        className="text-center py-8 text-sm text-slate-500"
      >
        Du kannst den Test jederzeit unter{" "}
        <button
          type="button"
          onClick={onStart}
          className="text-[#4563BB] font-semibold hover:underline"
          data-testid="cockpit-empty-state-dismissed-relink"
        >
          {"„Fokustyp-Test\""}
        </button>{" "}
        nachholen.
      </div>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="cockpit-empty-state"
      className="
        relative overflow-hidden
        rounded-3xl border border-amber-200
        bg-gradient-to-br from-amber-50 via-white to-orange-50
        px-6 py-12 sm:px-12 sm:py-16
        shadow-sm
      "
    >
      {/* Dekorative Highlight-Bubbles */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-12 w-64 h-64 rounded-full bg-amber-200/40 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-orange-200/30 blur-3xl"
      />

      <div className="relative max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100/80 border border-amber-200 text-xs font-bold text-amber-900 mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Dein erster Schritt
        </div>

        <h2
          data-testid="cockpit-empty-state-headline"
          className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[var(--fts-text-primary)] leading-tight"
        >
          Lerne deinen Fokustyp kennen
        </h2>

        <p
          data-testid="cockpit-empty-state-subline"
          className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto"
        >
          In 2 Minuten erfährst du, wie du kommunizierst — und wir können dir
          passende Empfehlungen, Flexpfade und Muster zeigen.
        </p>

        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={onStart}
            data-testid="cockpit-empty-state-cta"
            className="
              inline-flex items-center justify-center gap-2
              h-14 px-8 min-w-[260px]
              rounded-2xl
              bg-[#4563BB] hover:bg-[#3651A0]
              text-white text-base font-bold
              shadow-lg shadow-blue-900/10
              transition-all hover:scale-[1.02] active:scale-[0.98]
            "
          >
            Zeig mir meinen Fokustyp
            <ArrowRight className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={onLater}
            data-testid="cockpit-empty-state-later"
            className="text-sm text-slate-500 hover:text-slate-700 hover:underline transition-colors"
          >
            Oder später
          </button>
        </div>

        <p className="mt-10 text-xs text-slate-400">
          Dauert 2 Minuten · Keine Anmeldung mehr nötig · Komplett kostenfrei
        </p>
      </div>
    </motion.section>
  );
}
